import { useEffect, useState } from 'react'
import { ApiError } from '../lib/api.js'
import { useFocusTrap } from '../lib/focusTrap.js'
import { CloseMark } from './Marks.jsx'

/**
 * The review composer (PLAN §7, "Draft, edit, spoiler flag, delete").
 *
 * A modal over the album, on the same presentation as `RatingSheet` — a sheet
 * on a phone, a centred dialog on a desktop — because they are the same
 * gesture on the same page and two treatments would read as two features.
 *
 * **Draft and publish are two buttons, not a switch.** Saving a draft is the
 * cheap action and must never accidentally post; publishing is the one with a
 * consequence, so it is the one that says what it will do. Editing something
 * already published keeps it published — going back to a draft would silently
 * unpublish a thing people had already replied to.
 *
 * The Markdown help is a `<details>` rather than a toolbar. The syntax is six
 * things and a toolbar for six things is more chrome than typing them.
 */
export default function ReviewComposer({ open, review, subject, onClose, onSave }) {
  const [body, setBody] = useState('')
  const [spoiler, setSpoiler] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const dialogRef = useFocusTrap(open, onClose)

  // Reset from the review each time it opens, not on every render: typing
  // must not be clobbered by a parent re-rendering behind the dialog.
  useEffect(() => {
    if (!open) return
    setBody(review?.body_md || '')
    setSpoiler(Boolean(review?.spoiler))
    setError('')
  }, [open, review])

  if (!open) return null

  const published = review?.status === 'published'
  const empty = !body.trim()

  async function save(publish) {
    setBusy(true)
    setError('')
    try {
      await onSave({ body_md: body, spoiler, publish })
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="sheet composer"
        role="dialog"
        aria-modal="true"
        aria-label={`Review ${subject || 'this'}`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sheet-head">
          <div className="row-main">
            <h2 className="title-3 mb-0">{published ? 'Edit your review' : 'Write a review'}</h2>
            {subject ? <p className="footnote mb-0">{subject}</p> : null}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <CloseMark />
          </button>
        </header>

        <label className="field-label" htmlFor="review-body">
          Your review
        </label>
        <textarea
          id="review-body"
          className="composer-body"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={10}
          maxLength={10000}
          placeholder="What did it sound like? What changed on the third listen?"
        />

        <details className="composer-help">
          <summary className="footnote">Formatting</summary>
          <p className="footnote mb-0">
            <code>**bold**</code> · <code>*italic*</code> · <code>`code`</code> ·{' '}
            <code>- list</code> · <code>&gt; quote</code> · <code>[text](https://…)</code>
          </p>
          {/* The list above says what works. `markdown.py` leaves everything
              else as the text it was given — its documented honest failure
              mode — and until this line nothing on the screen said so, which
              is what L8 was actually about. In a JS string rather than JSX
              text so the apostrophe stays the ruled character. */}
          <p className="footnote mt-1 mb-0">
            {"Underscores, # headings and ~~strikethrough~~ aren't formatting here — they stay as you typed them."}
          </p>
        </details>

        <label className="composer-spoiler">
          <input
            type="checkbox"
            checked={spoiler}
            onChange={(event) => setSpoiler(event.target.checked)}
          />
          <span className="row-main">
            <span>Mark as a spoiler</span>
            <span className="footnote row-sub wrap">
              Readers see a cover until they choose to read it.
            </span>
          </span>
        </label>

        {error ? (
          <p className="form-alert error" role="alert">
            {error}
          </p>
        ) : null}

        <footer className="sheet-foot">
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => save(false)}
            disabled={busy}
          >
            {published ? 'Save' : 'Save draft'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => save(true)}
            disabled={busy || empty}
          >
            {published ? 'Save changes' : 'Publish'}
          </button>
        </footer>
      </div>
    </div>
  )
}
